import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NotificationModel, NotificationService } from "../../services/notification.service";
import { Router } from "@angular/router";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-notification-view',
  templateUrl: './notification-view.component.html',
  styleUrls: ['./notification-view.component.scss']
})
export class NotificationViewComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  notifications: NotificationModel[] = [];
  groupedNotifications: { date: string, items: NotificationModel[] }[] = [];
  filteredGrouped: { date: string, items: NotificationModel[] }[] = [];
  pagedNotifications: { date: string, items: NotificationModel[] }[] = [];

  userData = JSON.parse(localStorage.getItem('userData') || '{}');

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  searchTerm: string = '';
  startDate: any | null = null;
  endDate: any | null = null;

  pageSize = 5;
  pageIndex = 0;

  constructor(
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.notificationService.getUserNotifications(this.userData.email)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((data) => {
        this.notifications = data.sort(
          (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
        );
        this.groupByDate();
        this.applyFilters();
      });
  }

  // 🔹 Apply filters (search + date range)
  applyFilters() {
    const term = this.searchTerm.toLowerCase();

    this.filteredGrouped = this.groupedNotifications
      .map(group => ({
        date: group.date,
        items: group.items.filter((n: any) => {
          const notificationDate = n.time.toDate ? n.time.toDate() : new Date(n.time);
          const inDateRange =
            (!this.startDate || notificationDate >= this.startDate) &&
            (!this.endDate || notificationDate <= this.endDate);
          const matchesSearch =
            !term ||
            n.message.toLowerCase().includes(term) ||
            this.formatDate(n.time).toLowerCase().includes(term);

          return inDateRange && matchesSearch;
        })
      }))
      .filter(group => group.items.length > 0);

    // reset to first page when filters change
    this.pageIndex = 0;
    this.updatePagedNotifications();
  }

  // 🔹 Regroup notifications
  groupByDate() {
    const groups: { [key: string]: NotificationModel[] } = {};

    this.notifications.forEach(n => {
      const date = this.formatDate(n.time);
      if (!groups[date]) groups[date] = [];
      groups[date].push(n);
    });

    this.groupedNotifications = Object.keys(groups).map(date => ({
      date,
      items: groups[date]
    }));
  }

  // 🔹 Handle pagination manually
  updatePagedNotifications() {
    const allItems = this.filteredGrouped.flatMap(g =>
      g.items.map(i => ({ ...i, date: g.date }))
    );

    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const pagedItems = allItems.slice(startIndex, endIndex);

    const groups: { [key: string]: NotificationModel[] } = {};
    pagedItems.forEach(n => {
      if (!groups[n.date]) groups[n.date] = [];
      groups[n.date].push(n);
    });

    this.pagedNotifications = Object.keys(groups).map(date => ({
      date,
      items: groups[date]
    }));
  }

  // 🔹 On paginator change
  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.updatePagedNotifications();
  }

  formatDate(firestoreDate: any): string {
    const date = firestoreDate.toDate ? firestoreDate.toDate() : new Date(firestoreDate);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long' });
  }

  getTimeAgo(firestoreDate: any): string {
    const date = firestoreDate.toDate ? firestoreDate.toDate() : new Date(firestoreDate);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} days ago`;
    if (hours > 0) return `${hours} hours ago`;
    if (minutes > 0) return `${minutes} mins ago`;
    return 'Just now';
  }

  async markAsRead(notification: NotificationModel) {
    if (notification.status === 'msg-unread') {
      await this.notificationService.markAsRead(notification.id);
    }
  }

  async deleteNotification(notification: NotificationModel) {
    await this.notificationService.deleteNotification(notification.id);
    this.applyFilters(); // refresh list after delete
  }

  handleClick(notification: NotificationModel) {
    this.markAsRead(notification);
    if (notification.link) {
      this.router.navigate([notification.link]);
    }
  }
  get totalNotifications(): number {
    return this.filteredGrouped.reduce((count, group) => count + group.items.length, 0);
  }
  goBack() {
    this.router.navigate(['/home/dashboard']);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
